import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { CheckCircle, FileSignature, Loader2, Paperclip, Pencil, Send, X } from 'lucide-react';
import { directDraftsApi, documentsApi } from '../../services/api';
import { DraftIntakeChatResponse, DraftTypeInfo } from '../../types';
import { MicButton } from '../ui/MicButton';

interface DraftIntakeChatProps {
  spec: DraftTypeInfo;
  /** Generate the draft with what the chat gathered. */
  onGenerate: (data: {
    fields: Record<string, string>;
    instructions?: string;
    input_document_id?: string;
  }) => void;
  isGenerating: boolean;
  /** Known facts sent as the opening user turn (e.g. "Draft for this case"
      from a case file) — the interview skips whatever these already answer. */
  seed?: string;
  /** Document pre-attached before mount (e.g. "Draft a reply" from Documents) —
      the opening turn runs with it so its facts are extracted, never re-asked. */
  initialDoc?: { id: string; name: string } | null;
  /** Reports attach/detach so the parent's attach area can mirror the state. */
  onDocChange?: (doc: { id: string; name: string } | null) => void;
  /** Parent-triggerable file picker (the side-by-side attach area, spec §4.4). */
  attachOpenRef?: React.MutableRefObject<(() => void) | null>;
}

type Msg = { role: 'user' | 'assistant'; content: string };

/** App-initiated first turn — sent to the API, never rendered. */
const BEGIN = '__BEGIN__';
/** Fired after a document is attached mid-interview so the model re-reads it. */
const DOC_ATTACHED = '__DOCUMENT_ATTACHED__';
/** Turns the model treats as signals, not content — kept out of the visible thread. */
const SENTINELS = [BEGIN, DOC_ATTACHED];

/**
 * Guided drafting interview (July 2026 v2) — replaced the field form.
 * The ASSISTANT opens the conversation the moment a type is picked and asks
 * for each needed detail as a direct question, skipping anything the user
 * already volunteered or the attached document answers. Confirm card shows
 * everything captured before generation. Modeled on FramingChat.
 */
export function DraftIntakeChat({
  spec, onGenerate, isGenerating, seed, initialDoc, onDocChange, attachOpenRef,
}: DraftIntakeChatProps) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState('');
  const [latest, setLatest] = useState<DraftIntakeChatResponse | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [docId, setDocId] = useState<string | null>(initialDoc?.id ?? null);
  const [docName, setDocName] = useState<string | null>(initialDoc?.name ?? null);
  const [isUploading, setIsUploading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const beganRef = useRef(false);

  // The assistant speaks first (ref-guarded against StrictMode double-mount).
  // With a seed, the known case facts open the conversation instead, so the
  // interview only asks for what's still missing.
  useEffect(() => {
    if (!beganRef.current && messages.length === 0) {
      beganRef.current = true;
      send(seed?.trim() ? seed : BEGIN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isThinking, latest, confirmed, docName]);

  // Auto-grow composer (same behavior as FramingChat).
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, 128)}px`;
  }, [input, docName]);

  const onDrop = useCallback(async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setError('');
    setIsUploading(true);
    try {
      const { data } = await documentsApi.uploadDocument(file);
      setDocId(data.id);
      setDocName(file.name);
      onDocChange?.({ id: data.id, name: file.name });
      // Re-run the interview so the model reads the document and extracts what
      // it can instead of asking for facts that are sitting in the document.
      const answeredSoFar = messages.some(
        (m) => m.role === 'user' && !SENTINELS.includes(m.content),
      );
      if (!answeredSoFar) {
        // Still on the opener — restart cleanly with the document in hand so no
        // already-asked question lingers above the extracted result.
        setMessages([]);
        setLatest(null);
        setConfirmed(false);
        send(BEGIN, { docIdOverride: data.id, resetHistory: true });
      } else {
        // Mid-interview — keep typed answers, let the model fold in the document.
        send(DOC_ATTACHED, { docIdOverride: data.id });
      }
    } catch {
      setError('Could not upload that file. Please try again, or continue without it.');
    } finally {
      setIsUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // one upload rule everywhere: 50MB (spec §4.5)
  });

  // Let the parent's attach area open the same file picker (spec §4.4).
  useEffect(() => {
    if (!attachOpenRef) return;
    attachOpenRef.current = open;
    return () => {
      attachOpenRef.current = null;
    };
  }, [attachOpenRef, open]);

  const send = async (
    text: string,
    opts?: { docIdOverride?: string; resetHistory?: boolean },
  ) => {
    const content = text.trim();
    if (!content || isThinking) return;
    setError('');
    setConfirmed(false);
    // A freshly-uploaded id isn't in `docId` state yet (setState is async), so
    // the caller passes it explicitly when re-running after an attach.
    const effectiveDocId = opts?.docIdOverride ?? docId;
    const base = opts?.resetHistory ? [] : messages;
    const history: Msg[] = [...base, { role: 'user', content }];
    setMessages(history);
    setInput('');
    setIsThinking(true);
    try {
      const { data } = await directDraftsApi.intakeChat({
        draft_type: spec.type,
        messages: history.slice(-24),
        input_document_id: effectiveDocId || undefined,
      });
      setMessages([...history, { role: 'assistant', content: data.reply }]);
      setLatest(data);
      // The confirm card appears only when the INTERVIEW is over: the model
      // says ready (all fields filled or declined) or literally every spec
      // field has a value. Required-fields-done instead upgrades the
      // generate-now pill below the composer — it must not interrupt
      // questions still being asked.
      const allDone = spec.fields.every((f) => (data.fields?.[f.key] ?? '').trim());
      if (data.ready || allDone) setConfirmed(true);
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
        'The drafting assistant is unavailable right now — please try again in a moment.',
      );
      setMessages(history.slice(0, -1));
      // Don't put a hidden sentinel into the visible composer on failure.
      if (!SENTINELS.includes(content)) setInput(content);
    } finally {
      setIsThinking(false);
    }
  };

  const fieldLabel = (key: string) =>
    spec.fields.find((f) => f.key === key)?.label ?? key;

  const gathered = latest?.fields ?? {};
  const gatheredCount = spec.fields.filter((f) => (gathered[f.key] ?? '').trim()).length;
  const requiredMissing = spec.fields.filter((f) => f.required && !gathered[f.key]);
  const canEscape = !confirmed && !isThinking && !isGenerating && gatheredCount > 0;

  const generate = () =>
    onGenerate({
      fields: gathered,
      instructions: latest?.instructions || undefined,
      input_document_id: docId || undefined,
    });

  return (
    <div className="flex flex-col h-[34rem]">
      {/* thread — the hidden __BEGIN__ turn is sent to the API, never shown */}
      <div className="flex-1 overflow-y-auto px-1 py-2 space-y-4">
        {messages.filter((m) => !SENTINELS.includes(m.content)).map((m, i) => (
          <Bubble key={i} role={m.role}>{m.content}</Bubble>
        ))}

        {isThinking && (
          <Bubble role="assistant">
            <span className="inline-flex items-center gap-2 text-legal-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> noting that down…
            </span>
          </Bubble>
        )}

        {/* confirm card — everything captured, ready to generate */}
        {confirmed && (
          <div className="ml-4 sm:ml-10 border border-accent/40 bg-accent-50/70 rounded-2xl p-4 animate-slide-up">
            <p className="text-[11px] uppercase tracking-[0.18em] text-accent-700 mb-2 flex items-center gap-1.5">
              <FileSignature className="h-3.5 w-3.5" /> Ready to draft — {spec.label}
            </p>
            <dl className="space-y-1.5">
              {Object.entries(gathered).map(([key, value]) => (
                <div key={key} className="flex gap-2 text-sm">
                  <dt className="text-legal-500 shrink-0 min-w-[8rem]">{fieldLabel(key)}</dt>
                  <dd className="text-legal-900 break-words">{value}</dd>
                </div>
              ))}
            </dl>
            {latest?.instructions && (
              <p className="text-xs text-legal-600 mt-2.5">
                <span className="text-legal-500">Also noted:</span> {latest.instructions}
              </p>
            )}
            <p className="text-[11px] text-legal-500 mt-2.5">
              Statutory deadlines are checked automatically — any concerns appear with the draft.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                disabled={isGenerating}
                onClick={generate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors disabled:opacity-50"
              >
                {isGenerating
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Drafting… (30–60s)</>
                  : <><CheckCircle className="h-4 w-4" /> Generate draft</>}
              </button>
              <button
                type="button"
                onClick={() => setConfirmed(false)}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-legal-300 text-legal-600 rounded-lg text-sm hover:border-accent hover:text-accent-700 transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" /> Add more details
              </button>
            </div>
          </div>
        )}

        {error && (
          <p className="ml-4 sm:ml-10 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 break-words">{error}</p>
        )}
        <div ref={endRef} />
      </div>

      {/* composer */}
      <div {...getRootProps()} className="mt-3 border-t border-legal-100 pt-3">
        <input {...getInputProps()} />

        {/* Progress through the interview */}
        {!confirmed && gatheredCount > 0 && (
          <p className="mb-2 text-[11px] text-legal-400 text-right">
            {gatheredCount} of {spec.fields.length} details captured
          </p>
        )}

        {/* Types with a real attachment use-case lead with the affordance. */}
        {spec.attachment_hint && !docName && !isUploading && gatheredCount === 0 && (
          <button
            type="button"
            onClick={open}
            className="mb-2 w-full flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed border-accent/50 bg-accent-50/50 rounded-xl text-sm font-medium text-accent-800 hover:bg-accent-50 hover:border-accent transition-colors"
          >
            <Paperclip className="h-4 w-4" />
            {spec.attachment_hint}
          </button>
        )}

        {docName && (
          <div className="mb-2 flex items-center justify-between gap-2 px-3 py-2 bg-accent-50/70 border border-accent/30 rounded-xl">
            <span className="flex items-center gap-2 min-w-0 text-sm text-legal-700">
              <Paperclip className="h-3.5 w-3.5 text-accent-700 flex-shrink-0" />
              <span className="truncate">{docName}</span>
            </span>
            <button
              type="button"
              onClick={() => { setDocId(null); setDocName(null); onDocChange?.(null); }}
              aria-label="Remove attached document"
              className="text-legal-400 hover:text-legal-700 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(input); }}
          className="flex items-end gap-2"
        >
          <button
            type="button"
            onClick={open}
            disabled={isUploading}
            aria-label="Attach a document"
            title="Attach a document (PDF, DOCX, TXT — max 50MB)"
            className="p-2.5 border border-legal-300 text-legal-500 rounded-xl hover:border-accent hover:text-accent-700 transition-colors disabled:opacity-40"
          >
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>
          <MicButton
            track="mic-draft-intake"
            onFinal={(t) => setInput((v) => (v ? `${v} ` : '') + t)}
          />
          <textarea
            ref={taRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
            }}
            placeholder={
              docName
                ? 'Anything to add beyond the document?'
                : spec.attachment_hint && gatheredCount === 0
                ? 'Attach the document above, or describe the case…'
                : 'Describe the case…'
            }
            className="flex-1 resize-none px-3.5 py-2.5 border border-legal-300 rounded-xl text-sm focus:outline-none focus:ring-accent focus:border-accent max-h-32"
          />
          <button
            type="submit"
            disabled={!input.trim() || isThinking}
            aria-label="Send"
            className="p-2.5 bg-legal-900 text-accent rounded-xl hover:bg-legal-800 transition-colors disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>

        {/* Escape hatch — quiet while essentials are missing, prominent once
            the required fields are in (remaining questions are optional). */}
        {canEscape && (requiredMissing.length > 0 ? (
          <button
            type="button"
            onClick={generate}
            disabled={isGenerating}
            className="mt-2.5 w-full text-center text-xs text-legal-400 hover:text-accent-700 transition-colors disabled:opacity-50"
          >
            Generate now with what I've given ({requiredMissing.length} detail{requiredMissing.length === 1 ? '' : 's'} still blank — shown as [PLACEHOLDERS]) →
          </button>
        ) : (
          <button
            type="button"
            onClick={generate}
            disabled={isGenerating}
            className="mt-2.5 w-full text-center text-xs font-medium text-accent-800 bg-accent-50 border border-accent/30 rounded-lg py-2 hover:bg-accent-100 transition-colors disabled:opacity-50"
          >
            All essentials captured — generate now, or keep answering →
          </button>
        ))}
      </div>
    </div>
  );
}

function Bubble({ role, children }: { role: 'user' | 'assistant'; children: React.ReactNode }) {
  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-legal-900 text-legal-100 rounded-2xl rounded-br-md px-4 py-2.5 text-sm whitespace-pre-wrap break-words">
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2.5">
      <div className="h-7 w-7 rounded-full bg-accent-100 border border-accent/30 flex items-center justify-center flex-shrink-0 mt-0.5">
        <FileSignature className="h-3.5 w-3.5 text-accent-700" />
      </div>
      <div className="min-w-0 max-w-[85%] bg-legal-50 border border-legal-100 rounded-2xl rounded-tl-md px-4 py-2.5 text-sm text-legal-800 whitespace-pre-wrap break-words">
        {children}
      </div>
    </div>
  );
}
